<template>
  <v-dialog
    v-model="internalValue"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    scrollable
    tile
    rounded="0"
    :retain-focus="false"
  >
    <v-card tile rounded="0" class="fullscreen-dialog">
      <!-- Compact App Bar -->
      <v-card-title
        class="white--text"
        dark
        flat
        style="min-height: 64px !important; align-items: center"
        :class="['main-app-bar', { 'with-subtitle': subtitle }, color]"
      >
        <div class="toolbar-title-wrapper ml-2">
          <div class="toolbar-title">
            <v-icon left size="20" v-if="icon" color="white">{{ icon }}</v-icon>
            <span class="title-text">{{ title }}</span>
          </div>
          <div v-if="subtitle" class="toolbar-subtitle">
            {{ subtitle }}
          </div>
        </div>

        <slot name="app-bar-actions"></slot>

        <v-btn icon small class="close-btn" @click="close">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Content -->
      <v-card-text
        class="dialog-content px-0"
        :class="{ 'pt-4': !hidePadding }"
        :style="{ background: contentBackground }"
      >
        <slot name="filters"></slot>
        <div class="px-4"><slot></slot></div>
      </v-card-text>

      <!-- Sticky Footer -->
      <template v-if="!hideFooter">
        <v-divider></v-divider>

        <v-card-actions class="sticky-footer">
          <div class="footer-left">
            <slot name="footer-left"> </slot>
          </div>

          <v-spacer></v-spacer>

          <div class="footer-actions">
            <v-btn
              v-if="showLock"
              color="warning"
              depressed
              large
              dark
              :disabled="locked"
              :dark="!locked"
              class="mr-2"
              @click="$emit('lock')"
            >
              <v-icon left>
                {{ locked ? "mdi-lock" : "mdi-lock-open-outline" }}
              </v-icon>
              {{ locked ? $t("locked") : $t("lock") }}
            </v-btn>

            <slot name="footer-actions">
              <v-btn
                :color="color"
                :disabled="disabled"
                :loading="loading"
                :dark="!disabled"
                depressed
                large
                @click="$emit('save')"
              >
                <v-icon left>mdi-content-save</v-icon>
                {{ saveText }}
              </v-btn>
            </slot>
          </div>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "FullscreenDialog",
  props: {
    hidePadding: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: null,
    },
    icon: {
      type: String,
      default: null,
    },
    color: {
      type: String,
      default: "deep-purple",
    },
    toolbarColor: {
      type: String,
      default: "grey lighten-4",
    },
    contentBackground: {
      type: String,
      default: "#fafafa",
    },
    saveText: {
      type: String,
      default: "Sačuvaj promjene",
    },
    closeText: {
      type: String,
      default: "Zatvori",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showToolbar: {
      type: Boolean,
      default: true,
    },
    color: {
      type: String,
      default: "primary",
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    searchPlaceholder: {
      type: String,
      default: "Pretraži...",
    },
    fluidContainer: {
      type: Boolean,
      default: false,
    },
    showSaveInAppBar: {
      type: Boolean,
      default: false,
    },
    showAutosave: {
      type: Boolean,
      default: false,
    },
    lastSavedText: {
      type: String,
      default: "prije 2 minute",
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
    showLock: {
      type: Boolean,
      default: false,
    },
    locked: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchValue: "",
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  watch: {
    searchValue(val) {
      this.$emit("search", val);
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.fullscreen-dialog {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.main-app-bar {
  height: 56px !important;
  display: flex;
  align-items: flex-start;
  padding-top: 12px;
  padding-bottom: 12px;
}

.main-app-bar.with-subtitle {
  height: auto !important;
  min-height: 56px !important;
}

.toolbar-title-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-width: 0;
}

.close-btn {
  align-self: flex-start;
  flex-shrink: 0;
}

.toolbar-title {
  font-size: 1.3rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  line-height: 1.2;
}

.toolbar-subtitle {
  font-size: 0.85rem;
  font-weight: 400;
  opacity: 0.85;
  line-height: 1.3;
  margin-top: 2px;
}

.title-text {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  word-break: break-word;
}

.secondary-toolbar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  padding: 0 16px;
}

.search-field {
  max-width: 380px;
  margin-top: 0;
}

.dialog-content {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 20px 0;
  display: block;
}

.sticky-footer {
  position: sticky;
  bottom: 0;
  background: white;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
  padding: 16px 24px;
  z-index: 10;
  min-height: 64px;
}

.footer-left {
  display: flex;
  align-items: center;
}

.footer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Responsive */
@media (max-width: 600px) {
  .main-app-bar {
    height: auto !important;
    min-height: 48px !important;
    padding: 6px 4px 6px 8px;
  }

  .main-app-bar.with-subtitle {
    height: auto !important;
    min-height: 48px !important;
  }

  .toolbar-title-wrapper {
    padding-right: 4px;
    margin-left: 0 !important;
  }

  .toolbar-title {
    font-size: 0.95rem;
    align-items: flex-start;
  }

  .title-text {
    font-size: 0.95rem;
    -webkit-line-clamp: 3;
  }

  .toolbar-subtitle {
    font-size: 0.7rem;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .close-btn {
    margin-right: -4px;
  }

  .search-field {
    max-width: 100%;
  }

  .sticky-footer {
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px 16px;
  }

  .footer-left {
    width: 100%;
    justify-content: center;
  }

  .footer-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
